import { useMemo } from 'react';
import { Box, Tabs, Tab, CircularProgress } from '@mui/material';
import {
  PointhomeProfitLossData,
  ProfitLossContentType,
} from '../../../../types/pointhomeProfitLoss';
import PointhomeProfitLossContentTable from './PointhomeProfitLossContentTable';
import PointhomeProfitLossSummaryCards from './PointhomeProfitLossSummaryCards';
import PointhomeProfitLossChart from './PointhomeProfitLossChart';

import type { Dayjs } from 'dayjs';

type Props = {
  data: PointhomeProfitLossData | null;
  activeSubTab: number;
  onSubTabChange: (value: number) => void;
  selectedApp?: string | null;
  startDate: Dayjs;
  endDate: Dayjs;
};

type ContentConfig = {
  key: ProfitLossContentType;
  tabLabel: string;
  countLabel: string;
  cashLabel: string;
  cashTooltip: string;
  sheetName: string;
};

export const CONTENT_CONFIGS: ContentConfig[] = [
  {
    key: 'goldTicket',
    tabLabel: '황금티켓',
    countLabel: '황금티켓 오픈 수',
    cashLabel: '황금티켓 적립 원',
    cashTooltip: '황금티켓 발급시 지급된 캐시의 1/6 값입니다.',
    sheetName: '황금티켓',
  },
  {
    key: 'roulette',
    tabLabel: '티켓(룰렛)',
    countLabel: '티켓 오픈 수',
    cashLabel: '룰렛 적립 원',
    cashTooltip: '룰렛 발급시 지급된 캐시의 1/6 값입니다.',
    sheetName: '룰렛',
  },
  {
    key: 'attendanceBox',
    tabLabel: '출석체크',
    countLabel: '출석체크 오픈 수',
    cashLabel: '출석체크 적립 원',
    cashTooltip: '출석체크시 지급된 캐시의 1/6 값입니다.',
    sheetName: '출석체크',
  },
  {
    key: 'offerwall',
    tabLabel: '오퍼월',
    countLabel: '오퍼월 참여 수',
    cashLabel: '오퍼월 적립 원',
    cashTooltip: '오퍼월 참여시 지급된 캐시의 1/6 값입니다.',
    sheetName: '오퍼월',
  },
  {
    key: 'welcome',
    tabLabel: '웰컴',
    countLabel: '웰컴보상 지급 수',
    cashLabel: '웰컴보상 적립 원',
    cashTooltip:
      '웰컴보상 지급시 지급된 캐시의 1/6 값입니다. 웰컴보상은 연결된 매출원이 없어 매출은 항상 0으로 표시됩니다.',
    sheetName: '웰컴',
  },
  {
    key: 'phInteract',
    tabLabel: '해달 누르기',
    countLabel: '해달 누르기 수',
    cashLabel: '해달 누르기 적립 원',
    cashTooltip: '해달 누르기 지급시 지급된 캐시의 1/6 값입니다.',
    sheetName: '해달 누르기',
  },
  {
    key: 'phClean',
    tabLabel: '해달 청소하기',
    countLabel: '해달 청소하기 수',
    cashLabel: '해달 청소하기 적립 원',
    cashTooltip: '해달 청소하기 지급시 지급된 캐시의 1/6 값입니다.',
    sheetName: '해달 청소하기',
  },
  {
    key: 'phWash',
    tabLabel: '해달 설거지하기',
    countLabel: '해달 설거지하기 수',
    cashLabel: '해달 설거지하기 적립 원',
    cashTooltip: '해달 설거지하기 지급시 지급된 캐시의 1/6 값입니다.',
    sheetName: '해달 설거지하기',
  },
];

export default function PointhomeProfitLossDashboard({
  data,
  activeSubTab,
  onSubTabChange,
  selectedApp,
  startDate,
  endDate,
}: Props) {
  const activeConfig = CONTENT_CONFIGS[activeSubTab] ?? CONTENT_CONFIGS[0];

  // 탭별로 따로 호출하므로, 탭 전환 직후엔 이전 탭의 data가 남아있을 수 있다 → contentType으로 걸러낸다.
  const activeData = data?.contentType === activeConfig.key ? data : null;

  const summaryCardsData = useMemo(() => {
    if (!activeData) return null;
    const totalCost = Math.round(activeData.totalCash / 6);
    const totalProfit = activeData.totalRevenue - totalCost;
    const profitMargin =
      activeData.totalRevenue > 0 ? (totalProfit / activeData.totalRevenue) * 100 : 0;
    return { totalRevenue: activeData.totalRevenue, totalCost, totalProfit, profitMargin };
  }, [activeData]);

  return (
    <Box>
      {/* 하위 탭들 */}
      <Tabs
        value={activeSubTab}
        onChange={(_, newValue) => onSubTabChange(newValue)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 36,
            color: 'rgba(0, 0, 0, 0.6)',
            fontWeight: 'normal',
            px: 2,
            py: 1,
            borderRadius: '8px',
            mr: 1,
            '&.Mui-selected': {
              backgroundColor: 'rgba(0, 68, 210, 0.1)',
              color: '#0044D2',
              fontWeight: 'bold',
            },
          },
          '& .MuiTabs-indicator': {
            display: 'none',
          },
        }}
      >
        {CONTENT_CONFIGS.map((config) => (
          <Tab key={config.key} label={config.tabLabel} />
        ))}
      </Tabs>

      {activeData && summaryCardsData ? (
        <Box>
          <PointhomeProfitLossSummaryCards data={summaryCardsData} />
          <PointhomeProfitLossChart dailyStats={activeData.dailyStats} />
          <Box
            sx={{
              backgroundColor: '#ffffff',
              border: '1px solid #e0e0e0',
              borderRadius: 3,
              p: 2,
              mt: 3,
            }}
          >
            <PointhomeProfitLossContentTable
              rows={activeData.dailyStats}
              countLabel={activeConfig.countLabel}
              cashLabel={activeConfig.cashLabel}
              cashTooltip={activeConfig.cashTooltip}
              sheetName={activeConfig.sheetName}
              selectedApp={selectedApp}
              startDate={startDate}
              endDate={endDate}
            />
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <CircularProgress size={50} sx={{ color: '#0044D2' }} />
        </Box>
      )}
    </Box>
  );
}
